import React, { useState, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Alert,
  Stack,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  AttachFile as AttachIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const ALLOWED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.msg', '.eml', '.png', '.jpg', '.jpeg', '.gif', '.txt', '.csv',
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface PendingAttachmentsHandle {
  /** Returns the staged files to upload after request creation */
  getFiles: () => File[];
  /** Returns true if there are staged files */
  hasFiles: () => boolean;
}

interface PendingAttachmentsProps {
  /** When true the section shows a required marker and helper text. */
  required?: boolean;
  disabled?: boolean;
  files?: File[];
  onFilesChange?: (files: File[]) => void;
}

const PendingAttachments = forwardRef<PendingAttachmentsHandle, PendingAttachmentsProps>(({ required = false, disabled = false, files: controlledFiles, onFilesChange }, ref) => {
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const files = controlledFiles ?? localFiles;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const setFiles = (next: File[]) => { setLocalFiles(next); onFilesChange?.(next); };
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  useImperativeHandle(ref, () => ({
    getFiles: () => files,
    hasFiles: () => files.length > 0,
  }));

  const validateFile = (file: File): string | null => {
    if (file.size === 0 || file.name.length > 200 || file.name !== file.name.trim()
      || /[\u0000-\u001f\u007f\\/:*?"<>|]/.test(file.name)) {
      return `File "${file.name}" is empty or has an unsupported filename.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File "${file.name}" exceeds the 25 MB limit (${formatFileSize(file.size)}).`;
    }
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type "${ext}" is not allowed. Accepted: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    return null;
  };

  const addFiles = (newFiles: FileList | File[]) => {
    if (disabled) return;
    setError(null);
    const fileArray = Array.from(newFiles);
    for (const file of fileArray) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    const existingNames = new Set(files.map((f) => f.name));
    if (fileArray.some(f => existingNames.has(f.name))
      || new Set(fileArray.map(f => f.name)).size !== fileArray.length) {
      setError('A file with that name is already staged. Remove it before selecting a replacement.');
      return;
    }
    setFiles([...files, ...fileArray]);
  };

  const removeFile = (name: string) => {
    if (!disabled) setFiles(files.filter((f) => f.name !== name));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <AttachIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" fontWeight={600}>
          Attachments{required ? ' *' : ''}
        </Typography>
        {files.length > 0 && (
          <Chip label={files.length} size="small" color="primary" variant="outlined" />
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {required
          ? 'At least one attachment is required before this request can be submitted.'
          : 'Attach files before submitting. They will be uploaded after the request is created. Maximum 25 MB per file.'}
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <Box
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          borderRadius: 1,
          p: 2,
          mb: 1.5,
          textAlign: 'center',
          bgcolor: dragOver ? 'action.hover' : 'transparent',
          transition: 'all 0.2s',
          cursor: 'pointer',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileInput}
          disabled={disabled}
        />
        <UploadIcon color="action" sx={{ fontSize: 32, mb: 0.5 }} />
        <Button type="button" disabled={disabled} onClick={() => inputRef.current?.click()}>
          Choose attachments
        </Button>
        <Typography variant="body2" color="text.secondary">
          Drag & drop files here, or click to browse
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Max 25 MB · PDF, Office, images, email, text
        </Typography>
      </Box>

      {files.length > 0 && (
        <Stack spacing={0.5}>
          {files.map((file) => (
            <Stack
              key={file.name}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                py: 0.5,
                px: 1,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <FileIcon fontSize="small" color="action" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>{file.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(file.size)}
                </Typography>
              </Box>
              <Tooltip title="Remove">
                <IconButton size="small" disabled={disabled} aria-label={`Remove ${file.name}`} onClick={() => removeFile(file.name)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
});

PendingAttachments.displayName = 'PendingAttachments';
export default PendingAttachments;
